import React, { useState, useEffect } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import "./DiceRoller.css";

// Type guard helper for dice roll data
const getDiceDataProperty = (data: unknown, prop: string): unknown => {
  if (typeof data === "object" && data !== null && prop in data) {
    return (data as Record<string, unknown>)[prop];
  }
  return undefined;
};

interface DiceRollerProps {
  className?: string;
  characterId?: string;
  characterName?: string;
  defaultPrivate?: boolean;
  sessionId?: string;
}

interface DiceRollResult {
  formula: string;
  result: number;
  breakdown: string;
  timestamp: string;
  rollName?: string;
  isPrivate: boolean;
  isCritical?: boolean;
  characterId?: string;
  characterName?: string;
  rollerId: string;
}

interface DicePreset {
  name: string;
  formula: string;
  description: string;
}

const commonDicePresets: DicePreset[] = [
  { name: "D20", formula: "1d20", description: "Standard D20 roll" },
  { name: "D20 + Modifier", formula: "1d20+", description: "D20 with modifier (enter after +)" },
  { name: "Advantage", formula: "2d20kh1", description: "Roll 2d20, keep highest" },
  { name: "Disadvantage", formula: "2d20kl1", description: "Roll 2d20, keep lowest" },
  { name: "Ability Score", formula: "4d6kh3", description: "Roll 4d6, keep highest 3" },
  { name: "Initiative", formula: "1d20+dex", description: "Initiative roll" },
  { name: "D100", formula: "1d100", description: "Percentile roll" },
  { name: "Damage (1d8)", formula: "1d8", description: "Basic weapon damage" },
  { name: "Fireball", formula: "8d6", description: "Fireball damage" },
];

export const DiceRoller: React.FC<DiceRollerProps> = ({
  className = "",
  characterId,
  characterName,
  defaultPrivate = false,
  sessionId,
}) => {
  const { isConnected, sendDiceRoll, getDiceRolls } = useWebSocketContext();

  const [formula, setFormula] = useState("1d20");
  const [rollName, setRollName] = useState("");
  const [isPrivate, setIsPrivate] = useState(defaultPrivate);
  const [showPresets, setShowPresets] = useState(false);
  const [lastRoll, setLastRoll] = useState<DiceRollResult | null>(null);

  const diceRolls = getDiceRolls(sessionId);
  const latestRoll = diceRolls.length > 0 ? diceRolls[diceRolls.length - 1] : null;

  // Update last roll when a *new* dice roll arrives. Keyed on the roll's id (not the
  // array, which is a fresh reference every render) to avoid a setState→render loop.
  useEffect(() => {
    if (latestRoll?.data && typeof latestRoll.data === "object") {
      const rawResults = getDiceDataProperty(latestRoll.data, "results");
      const breakdown = Array.isArray(rawResults) ? (rawResults as number[]).join(" + ") : "";
      const rollName = getDiceDataProperty(latestRoll.data, "rollName");
      const characterId = getDiceDataProperty(latestRoll.data, "characterId");
      const characterName = getDiceDataProperty(latestRoll.data, "characterName");
      setLastRoll({
        formula: String(getDiceDataProperty(latestRoll.data, "formula") || ""),
        result: Number(getDiceDataProperty(latestRoll.data, "total") || 0),
        breakdown,
        timestamp: latestRoll.timestamp,
        rollName: rollName != null ? String(rollName) : undefined,
        isPrivate: Boolean(getDiceDataProperty(latestRoll.data, "isPrivate")),
        isCritical: Boolean(getDiceDataProperty(latestRoll.data, "isCritical")),
        characterId: characterId != null ? String(characterId) : undefined,
        characterName: characterName != null ? String(characterName) : undefined,
        rollerId: latestRoll.senderId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestRoll?.id, latestRoll?.timestamp]);

  const handleRoll = () => {
    if (!formula.trim() || !isConnected) return;

    const success = sendDiceRoll(
      formula.trim(),
      rollName.trim() || undefined,
      isPrivate,
      characterId,
      sessionId,
      characterName
    );

    if (success) {
      // Clear roll name after successful roll
      setRollName("");
    }
  };

  const handlePresetSelect = (preset: DicePreset) => {
    setFormula(preset.formula);
    setShowPresets(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleRoll();
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getResultColor = (result: number, formula: string, isCritical?: boolean): string => {
    if (isCritical) return "text-green-400 font-bold"; // Critical success

    // Check for fumble: raw d20 showing 1
    const isD20Roll =
      formula.toLowerCase().includes("d20") || formula.toLowerCase().includes("1d20");
    if (isD20Roll && result === 1) return "text-red-400 font-bold"; // Critical failure

    return "text-blue-300 font-semibold";
  };

  return (
    <div className={`dice-roller bg-gray-800 rounded-lg border border-gray-600 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg flex items-center">
          🎲 Dice Roller
          {!isConnected && <span className="ml-2 text-red-400 text-sm">(Offline)</span>}
        </h3>
        <button
          onClick={() => setShowPresets(!showPresets)}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          {showPresets ? "Hide" : "Show"} Presets
        </button>
      </div>

      {/* Presets */}
      {showPresets && (
        <div className="mb-4 p-3 bg-gray-700 rounded-lg">
          <h4 className="text-gray-300 text-sm font-medium mb-2">Quick Presets:</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {commonDicePresets.map((preset) => (
              <button
                key={preset.name}
                onClick={() => handlePresetSelect(preset)}
                className="p-2 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded transition-colors text-left"
                title={preset.description}
              >
                <div className="font-medium">{preset.name}</div>
                <div className="text-gray-400 text-xs">{preset.formula}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Roll Configuration */}
      <div className="space-y-3 mb-4">
        {/* Dice Formula */}
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-1">Dice Formula</label>
          <input
            type="text"
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., 1d20+5, 3d6, 2d10kh1"
            className="w-full px-3 py-2 bg-gray-700 text-white placeholder-gray-400 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
          />
          <div className="text-gray-500 text-xs mt-1">
            Examples: 1d20+3, 3d6, 2d20kh1 (advantage), 4d6kh3, 1d100
          </div>
        </div>

        {/* Roll Name */}
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-1">
            Roll Name (optional)
          </label>
          <input
            type="text"
            value={rollName}
            onChange={(e) => setRollName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Attack Roll, Perception Check"
            className="w-full px-3 py-2 bg-gray-700 text-white placeholder-gray-400 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Options */}
        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="rounded border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-300 text-sm">Private Roll (GM only)</span>
          </label>

          <button
            onClick={handleRoll}
            disabled={!isConnected || !formula.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors font-medium"
          >
            🎲 Roll
          </button>
        </div>
      </div>

      {/* Last Roll Result */}
      {lastRoll && (
        <div className="mt-4 p-3 bg-gray-700 rounded-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-white font-medium">
              {lastRoll.characterName && (
                <span className="text-purple-300 mr-1">{lastRoll.characterName}:</span>
              )}
              {lastRoll.rollName ? lastRoll.rollName : "Dice Roll"}
              {lastRoll.isPrivate && (
                <span className="ml-2 text-purple-400 text-xs">[Private]</span>
              )}
            </h4>
            <span className="text-gray-400 text-xs">{formatTimestamp(lastRoll.timestamp)}</span>
          </div>

          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="text-gray-300">Formula:</span>
              <span className="font-mono text-yellow-300">{lastRoll.formula}</span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-gray-300">Result:</span>
              <span
                className={`text-2xl font-bold ${getResultColor(lastRoll.result, lastRoll.formula, lastRoll.isCritical)}`}
              >
                {lastRoll.result}
              </span>
              {lastRoll.isCritical && (
                <span className="text-green-400 text-xs font-bold animate-pulse">✨ CRIT!</span>
              )}
            </div>

            {lastRoll.breakdown && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-300">Rolls:</span>
                <span className="font-mono text-green-300 text-sm">[{lastRoll.breakdown}]</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Rolls History */}
      {diceRolls.length > 1 && (
        <div className="mt-4">
          <h4 className="text-gray-300 text-sm font-medium mb-2">Recent Rolls:</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {diceRolls
              .slice(-5)
              .reverse()
              .map((roll, index) => (
                <div
                  key={`${roll.id}-${index}`}
                  className="text-xs text-gray-400 flex justify-between"
                >
                  <span>
                    {String(
                      getDiceDataProperty(roll.data, "rollName") ||
                        getDiceDataProperty(roll.data, "formula") ||
                        ""
                    )}
                    :
                    <span className="text-white ml-1">
                      {String(getDiceDataProperty(roll.data, "total") ?? "")}
                    </span>
                  </span>
                  <span>{formatTimestamp(roll.timestamp)}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
